/* eslint-disable @typescript-eslint/naming-convention */
import type { ValueOf } from 'type-fest';

import type { Locale } from '@change-corgi/config/locales';

import { intlRelativeTimeFormatLocaleMapping } from 'config/intl-relativetimeformat';

import { loadMissingFeatures } from '../polyfills';
import type { IntlCheck } from '../types';

const importMapping: Record<ValueOf<typeof intlRelativeTimeFormatLocaleMapping>, () => Promise<unknown>> = {
	en: async () =>
		import(
			/* webpackChunkName: "polyfill/intl-relativetimeformat/en" */ '@formatjs/intl-relativetimeformat/locale-data/en.js'
		),
	'en-AU': async () =>
		import(
			/* webpackChunkName: "polyfill/intl-relativetimeformat/en-AU" */ '@formatjs/intl-relativetimeformat/locale-data/en-AU.js'
		),
	'en-CA': async () =>
		import(
			/* webpackChunkName: "polyfill/intl-relativetimeformat/en-CA" */ '@formatjs/intl-relativetimeformat/locale-data/en-CA.js'
		),
	'en-GB': async () =>
		import(
			/* webpackChunkName: "polyfill/intl-relativetimeformat/en-GB" */ '@formatjs/intl-relativetimeformat/locale-data/en-GB.js'
		),
	'en-IN': async () =>
		import(
			/* webpackChunkName: "polyfill/intl-relativetimeformat/en-IN" */ '@formatjs/intl-relativetimeformat/locale-data/en-IN.js'
		),
	de: async () =>
		import(
			/* webpackChunkName: "polyfill/intl-relativetimeformat/de" */ '@formatjs/intl-relativetimeformat/locale-data/de.js'
		),
	fr: async () =>
		import(
			/* webpackChunkName: "polyfill/intl-relativetimeformat/fr" */ '@formatjs/intl-relativetimeformat/locale-data/fr.js'
		),
	hi: async () =>
		import(
			/* webpackChunkName: "polyfill/intl-relativetimeformat/hi" */ '@formatjs/intl-relativetimeformat/locale-data/hi.js'
		),
	it: async () =>
		import(
			/* webpackChunkName: "polyfill/intl-relativetimeformat/it" */ '@formatjs/intl-relativetimeformat/locale-data/it.js'
		),
	ja: async () =>
		import(
			/* webpackChunkName: "polyfill/intl-relativetimeformat/ja" */ '@formatjs/intl-relativetimeformat/locale-data/ja.js'
		),
	ko: async () =>
		import(
			/* webpackChunkName: "polyfill/intl-relativetimeformat/ko" */ '@formatjs/intl-relativetimeformat/locale-data/ko.js'
		),
	pt: async () =>
		import(
			/* webpackChunkName: "polyfill/intl-relativetimeformat/pt" */ '@formatjs/intl-relativetimeformat/locale-data/pt.js'
		),
	ru: async () =>
		import(
			/* webpackChunkName: "polyfill/intl-relativetimeformat/ru" */ '@formatjs/intl-relativetimeformat/locale-data/ru.js'
		),
	es: async () =>
		import(
			/* webpackChunkName: "polyfill/intl-relativetimeformat/es" */ '@formatjs/intl-relativetimeformat/locale-data/es.js'
		),
	'es-419': async () =>
		import(
			/* webpackChunkName: "polyfill/intl-relativetimeformat/es-419" */ '@formatjs/intl-relativetimeformat/locale-data/es-419.js'
		),
	'es-AR': async () =>
		import(
			/* webpackChunkName: "polyfill/intl-relativetimeformat/es-AR" */ '@formatjs/intl-relativetimeformat/locale-data/es-AR.js'
		),
	id: async () =>
		import(
			/* webpackChunkName: "polyfill/intl-relativetimeformat/id" */ '@formatjs/intl-relativetimeformat/locale-data/id.js'
		),
	tr: async () =>
		import(
			/* webpackChunkName: "polyfill/intl-relativetimeformat/tr" */ '@formatjs/intl-relativetimeformat/locale-data/tr.js'
		),
	th: async () =>
		import(
			/* webpackChunkName: "polyfill/intl-relativetimeformat/th" */ '@formatjs/intl-relativetimeformat/locale-data/th.js'
		),
};

export async function loadPolyfill(locale: string, deps: readonly IntlCheck[]): Promise<void> {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const intlLocale = intlRelativeTimeFormatLocaleMapping[locale as Locale];
	await loadMissingFeatures(deps, locale);
	await import(
		/* webpackChunkName: "polyfill/intl-relativetimeformat/polyfill" */ '@formatjs/intl-relativetimeformat/polyfill.js'
	);
	await importMapping[intlLocale]();
}
